import config, { getApiKey } from '../config/environment';
import {
  formatJsonDocument,
  isUntypedJsonDocument,
  SseEventAssembler,
  tryParseJsonDocument,
} from '../utils/sseEventParser';
import { dispatchQualification } from '../utils/qualification';
import { detectLockout, signalLockout } from '../utils/lockout';

/**
 * An in-stream failure can arrive as a bare `{"error": "..."}` document with no
 * `type`, so the typed-frame dispatch never sees it. Only treat such a document
 * as a failure when it carries nothing but the error envelope's own keys — a
 * structured-output agent may legitimately answer with an `error` key.
 */
const ERROR_ENVELOPE_KEYS = new Set(['error', 'status_code', 'path', 'method', 'detail']);

const isBareErrorDocument = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if (typeof value.error !== 'string' || value.error.trim() === '') return false;
  return Object.keys(value).every((key) => ERROR_ENVELOPE_KEYS.has(key));
};

class StreamingService {
  constructor() {
    this.activeStreams = new Map(); // Track active streams by session ID
  }

  /**
   * Execute agent with streaming response
   * @param {string} agentId - The agent ID
   * @param {string} query - The user query
   * @param {Object} variables - Variables to pass to the agent
   * @param {string} sessionId - Session ID for conversation continuity
   * @param {string} userLocation - User's location
   * @param {string} userTimezone - User's timezone
   * @param {Array<File>} files - Files to upload and process
   * @param {Function} onMessage - Callback for each message chunk
   * @param {Function} onError - Callback for errors
   * @param {Function} onComplete - Callback when stream completes
   * @param {Function} onRunMeta - Optional callback when run_id is known
   * @param {Object} hilCallbacks - Optional HIL workflow callbacks
   * @param {Function} onBackgroundStarted - Optional callback for background workflow started
   * @param {Object} loopCallbacks - Optional loop-node callbacks ({ onLoopPreStep, onBatchJobStarted })
   * @param {Function} onToolWarning - Optional callback for the tool_warning frame
   * @param {Object} frameCallbacks - Optional metadata-frame callbacks ({ onSources, onFollowups, onRunError })
   * @param {Object} qualification - Optional answer/cancel for a pending qualifying question ({ qualification_id, action, answers })
   * @param {Object} qualificationCallbacks - Optional ({ onQualificationRequired, onQualificationCancelled, onQualificationLimitReached })
   * @returns {Promise} - Promise that resolves when stream starts
   */
  async executeAgentStream(agentId, query, variables = {}, sessionId = null, userLocation = null, userTimezone = null, files = [], onMessage, onError, onComplete, onRunMeta, hilCallbacks = null, onBackgroundStarted = null, filters = null, streaming = true, teamCallbacks = null, loopCallbacks = null, onToolWarning = null, frameCallbacks = null, qualification = null, qualificationCallbacks = null) {
    const onSources = frameCallbacks?.onSources || null;
    const onFollowups = frameCallbacks?.onFollowups || null;
    const onRunError = frameCallbacks?.onRunError || null;

    try {
      // Close any existing stream for this session
      if (sessionId && this.activeStreams.has(sessionId)) {
        this.closeStream(sessionId);
      }

      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append('agent_id', agentId);
      formData.append('query', query);
      formData.append('session_id', sessionId || this.generateSessionId());
      formData.append('streaming', String(streaming));

      // Add variables as JSON string
      if (variables && Object.keys(variables).length > 0) {
        formData.append('variables', JSON.stringify(variables));
      }

      // Add user_location and user_timezone if provided
      if (userLocation) {
        formData.append('user_location', userLocation);
      }
      if (userTimezone) {
        formData.append('user_timezone', userTimezone);
      }

      // Add files if provided
      if (files && files.length > 0) {
        files.forEach((file) => {
          formData.append('files', file);
        });
      }

      // Add filters if provided
      if (filters && Object.keys(filters).length > 0) {
        formData.append('filters', JSON.stringify(filters));
      }

      // Answering or cancelling a pending qualifying question. Must be a JSON
      // string — a bare object serialises to "[object Object]" in FormData.
      if (qualification && typeof qualification === 'object') {
        formData.append('qualification', JSON.stringify(qualification));
      }

      // Create the request
      const response = await fetch(`${config.API_BASE_URL}/dynamic-agent/execute`, {
        method: 'POST',
        headers: {
          'Accept': streaming ? 'text/event-stream' : 'application/json',
          'Cache-Control': 'no-cache',
          'x-api-key': getApiKey(),
          // Don't set Content-Type - browser will set it automatically with boundary for multipart/form-data
        },
        body: formData
      });

      if (!response.ok) {
        // Surface the backend's detail message (e.g. loop-node validation errors such as
        // "The step before the loop node did not produce a JSON array of records").
        let detail = null;
        let errorBody = null;
        try {
          errorBody = await response.json();
          detail = errorBody?.detail || errorBody?.message || errorBody?.error || null;
        } catch (_) {}
        // fetch bypasses the axios interceptor, so raise lockouts (§9) here too.
        const lockoutKind = detectLockout(response.status, errorBody);
        if (lockoutKind) signalLockout(lockoutKind);
        const httpError = new Error(detail || `HTTP error! status: ${response.status}`);
        httpError.status = response.status;
        httpError.data = errorBody;
        throw httpError;
      }

      return await this.consumeAgentStream(response, {
        sessionId,
        streaming,
        onMessage,
        onError,
        onComplete,
        onRunMeta,
        hilCallbacks,
        onBackgroundStarted,
        teamCallbacks,
        loopCallbacks,
        onToolWarning,
        onSources,
        onFollowups,
        onRunError,
        qualificationCallbacks,
      });
    } catch (error) {
      console.error('Streaming error:', error);
      if (onError) {
        onError(error);
      }

      // Clean up on error
      if (sessionId) {
        this.activeStreams.delete(sessionId);
      }
    }
  }

  /**
   * Consume an agent SSE response body.
   *
   * The backend interleaves JSON metadata frames (run_meta, sources, followups,
   * tool_warning, run_error) with plain answer text, so every payload is probed
   * as JSON first and only appended to the answer when it is not one of ours.
   *
   * Shared by /execute and the run continuation endpoints (regenerate, fork).
   *
   * @param {Response} response - fetch Response with an SSE (or JSON) body
   * @param {Object} options - callbacks; see executeAgentStream
   */
  async consumeAgentStream(response, options = {}) {
    const {
      sessionId = null,
      streaming = true,
      onMessage = null,
      onError = null,
      onComplete = null,
      onRunMeta = null,
      hilCallbacks = null,
      onBackgroundStarted = null,
      teamCallbacks = null,
      loopCallbacks = null,
      onToolWarning = null,
      onSources = null,
      onFollowups = null,
      onRunError = null,
      qualificationCallbacks = null,
    } = options;

    // run_id can be emitted from header/body/SSE; dedupe same run_id but allow new ids.
    let lastRunMetaId = null;
    let sentRunMetaWithoutId = false;
    const emitRunMeta = (meta, source) => {
      if (!onRunMeta || !meta) return;
      const runId = typeof meta.run_id === 'string' && meta.run_id.trim() !== ''
        ? meta.run_id
        : null;

      if (runId) {
        if (runId === lastRunMetaId) return;
        lastRunMetaId = runId;
        sentRunMetaWithoutId = false;
        onRunMeta({ ...meta, source });
        return;
      }

      if (sentRunMetaWithoutId) return;
      sentRunMetaWithoutId = true;
      onRunMeta({ ...meta, source });
    };

    try {
      const headerRunId = response.headers?.get?.('X-Agent-Run-Id') || response.headers?.get?.('x-agent-run-id');
      if (headerRunId) {
        emitRunMeta({ run_id: headerRunId }, 'header');
      }
    } catch (e) {
      // ignore header parsing issues
    }

    // Non-streaming: parse JSON response and invoke callbacks directly
    if (!streaming) {
      const json = await response.json();

      if (json.run_id) {
        emitRunMeta({ run_id: json.run_id }, 'body');
      }

      // A qualifying question (or a cancel/limit outcome) replaces the answer;
      // it arrives inside the `response` envelope on the primary endpoints and
      // at the top level on execute-background.
      if (dispatchQualification(json, qualificationCallbacks)) {
        return;
      }

      // Large loop datasets return a batch_job_started object as the response body.
      if (json.type === 'batch_job_started' && loopCallbacks?.onBatchJobStarted) {
        loopCallbacks.onBatchJobStarted(json);
        return;
      }

      const content = typeof json.response === 'string'
        ? json.response
        : JSON.stringify(json.response, null, 2);

      if (onMessage) {
        onMessage(content, content);
      }
      if (onComplete) {
        onComplete(content);
      }
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullMessage = '';
    /** Last raw token appended (after SSE unwrap); used for safe inter-token spacing. */
    let lastStreamChunk = '';
    let streamAbortedByErrorEvent = false;
    let isClosed = false;
    const sseAssembler = new SseEventAssembler();

    /** Throttle onMessage to reduce markdown re-parses mid-stream (fullMessage is always complete). */
    const ON_MESSAGE_DEBOUNCE_MS = 50;
    let onMessageFlushTimer = null;
    let pendingOnMessageDelta = '';

    const flushOnMessagePending = () => {
      if (onMessageFlushTimer) {
        clearTimeout(onMessageFlushTimer);
        onMessageFlushTimer = null;
      }
      if (pendingOnMessageDelta !== '' && onMessage) {
        const batchedDelta = pendingOnMessageDelta;
        pendingOnMessageDelta = '';
        onMessage(batchedDelta, fullMessage);
      }
    };

    const scheduleOnMessage = (delta) => {
      if (!onMessage || delta === '') return;
      pendingOnMessageDelta += delta;
      if (!onMessageFlushTimer) {
        onMessageFlushTimer = setTimeout(() => {
          const batchedDelta = pendingOnMessageDelta;
          pendingOnMessageDelta = '';
          onMessageFlushTimer = null;
          if (batchedDelta !== '') {
            onMessage(batchedDelta, fullMessage);
          }
        }, ON_MESSAGE_DEBOUNCE_MS);
      }
    };

    // Store the stream for potential cleanup
    if (sessionId) {
      this.activeStreams.set(sessionId, {
        reader,
        controller: reader
      });
    }

    const isInsideCodeFence = (text) => {
      if (!text) return false;
      const fenceMatches = text.match(/```/g);
      return (fenceMatches?.length || 0) % 2 === 1;
    };

    const getLastLine = (text) => (text.split('\n').pop() || '');

    /** True when the buffer ends mid-URL (http/https scheme started, no whitespace yet). */
    const endsInsideUrl = (text) => {
      if (!text) return false;
      return /https?:\/\/[^\s]*$/.test(text);
    };

    /** Next SSE token continues a split URL path/query rather than prose after the link. */
    const looksLikeUrlContinuation = (incoming) => {
      if (!incoming) return false;
      const trimmed = incoming.trimStart();
      if (!trimmed) return false;
      const head = trimmed.charAt(0);
      if (/[/?#&=.%_-]/.test(head)) return true;
      if (/[a-z0-9]/.test(head)) return true;
      if (/^[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]+$/.test(trimmed)) return true;
      return false;
    };

    /** Prose glued to a completed URL (e.g. "/edit" + "Do you want") must break before autolink runs. */
    const shouldPrefixNewlineAfterUrl = (currentText, incoming) => {
      if (!currentText || !incoming) return false;
      if (isInsideCodeFence(currentText)) return false;
      if (!endsInsideUrl(currentText)) return false;
      if (/^\s/.test(incoming)) return false;
      if (looksLikeUrlContinuation(incoming)) return false;
      if (currentText.endsWith('\n')) return false;
      return true;
    };

    /** Line is only unicode box-drawing / block separators (e.g. ─════ variants). */
    const isOnlyBoxDrawingLine = (line) => {
      if (!line) return false;
      const t = line.trim();
      if (t.length < 2) return false;
      if (!/[\u2500-\u257F]/.test(t)) return false;
      return /^[\s\u2500-\u257F]+$/.test(line.replace(/\r/g, ''));
    };

    const incomingIsBoxDrawingOnly = (incoming) => {
      const t = incoming.trim();
      if (t.length < 2) return false;
      if (!/[\u2500-\u257F]/.test(incoming)) return false;
      return /^[\s\u2500-\u257F]+$/.test(incoming.replace(/\r/g, ''));
    };

    const shouldPrefixNewlineAfterDecorativeLine = (currentText, incoming) => {
      if (!currentText || !incoming) return false;
      if (isInsideCodeFence(currentText)) return false;
      if (/^\s/.test(incoming)) return false;
      if (currentText.endsWith('\n')) return false;
      const lastLine = getLastLine(currentText);
      if (!isOnlyBoxDrawingLine(lastLine)) return false;
      if (incomingIsBoxDrawingOnly(incoming)) return false;
      return true;
    };

    /**
     * Streams often send opening strong/emphasis as its own token: `**` or `__` then body text.
     * If the next chunk starts content without a leading newline, glue reads as `**TITLE` on one line.
     */
    const shouldPrefixNewlineAfterLoneEmphasisDelimiter = (currentText, incoming) => {
      if (!currentText || !incoming) return false;
      if (isInsideCodeFence(currentText)) return false;
      if (/^\s/.test(incoming)) return false;
      if (currentText.endsWith('\n')) return false;
      const lastLineTrim = getLastLine(currentText).trim();
      if (lastLineTrim !== '**' && lastLineTrim !== '__') return false;
      // Next token looks like real text (not another `*`/`_` fence fragment)
      if (!/^[A-Za-z0-9(['"`]/.test(incoming)) return false;
      return true;
    };

    const isLikelyMarkdownBlockStart = (incoming) => {
      if (!incoming) return false;
      const trimmedAll = incoming.trim();
      // Decorative separator lines (box drawing ─═ etc.); keep off the prior heading line.
      if (
        trimmedAll.length >= 4 &&
        /^[\s\u2500-\u257F]+$/.test(incoming) &&
        /[\u2500-\u257F]/.test(incoming)
      ) {
        return true;
      }
      return (
        /^#{1,6}$/.test(incoming) ||
        /^#{1,6}\s/.test(incoming) ||
        /^(-{3,}|\*{3,}|_{3,})$/.test(incoming) ||
        /^```/.test(incoming) ||
        /^`{1,3}$/.test(incoming) ||
        /^>\s?/.test(incoming) ||
        /^[-*+]\s/.test(incoming) ||
        // Split tokens: "-" or ">" alone on an SSE event
        trimmedAll === '-' ||
        trimmedAll === '>' ||
        /^\d+\.\s/.test(incoming) ||
        /^\|/.test(incoming) ||
        /^<\s*(div|h[1-6]|ul|ol|li|table|pre|code)\b/i.test(incoming)
      );
    };

    const shouldPrefixMarkdownNewline = (currentText, incoming) => {
      if (!currentText || !incoming) return false;

      // Lone "-" list marker: never force a break inside number ranges (3 + - + 6) or hyphenated words.
      if (incoming.trim() === '-' && !currentText.endsWith('\n')) {
        const last = currentText.slice(-1);
        if (/[\dA-Za-z]/.test(last)) return false;
        return true;
      }

      // Continue a decorative ─═ row across tokens (do not inject \n between chunks).
      if (!isInsideCodeFence(currentText)) {
        const lastLine = getLastLine(currentText);
        if (
          isOnlyBoxDrawingLine(lastLine) &&
          incomingIsBoxDrawingOnly(incoming)
        ) {
          return false;
        }
      }

      // Fence fragments can arrive as ` + `` (or inverse). Keep them on their own line
      // unless they are a continuation of an existing fragment.
      if (/^`{1,3}$/.test(incoming)) {
        const lastTrimmed = getLastLine(currentText).trimEnd();
        if (lastTrimmed === '') return false;
        if (/`{1,2}$/.test(lastTrimmed)) return false;
        return true;
      }

      if (isInsideCodeFence(currentText)) {
        // Do not enforce markdown block rules inside code, except for explicit fence markers.
        if (!/^```/.test(incoming)) return false;
        return !currentText.endsWith('\n');
      }

      if (!isLikelyMarkdownBlockStart(incoming)) return false;

      // Only force a newline when we're in the middle of a line.
      return !currentText.endsWith('\n');
    };

    const shouldStartCodeBodyAfterFenceLanguage = (currentText, incoming) => {
      if (!currentText || !incoming) return false;
      if (/^\s/.test(incoming)) return false;
      return /^```[A-Za-z0-9_-]+$/.test(getLastLine(currentText).trimEnd());
    };

    const shouldStartParagraphAfterHeading = (currentText, incoming) => {
      if (!currentText || !incoming) return false;
      if (isInsideCodeFence(currentText)) return false;
      if (/^\s/.test(incoming)) return false;

      const lastLine = getLastLine(currentText);
      if (!/^#{1,6}\s+/.test(lastLine)) return false;
      if (currentText.endsWith('\n')) return false;
      if (isLikelyMarkdownBlockStart(incoming)) return false;
      if (/^[\])}.,!?;:%]/.test(incoming)) return false;
      if (/[([{]$/.test(lastLine.trimEnd())) return false;

      // Avoid splitting heading words streamed across chunks:
      // e.g. "Ph" + "ases" should remain in one heading line.
      const trimmedIncoming = incoming.trimStart();
      if (!/^[A-Z0-9<]/.test(trimmedIncoming)) return false;

      return true;
    };

    const shouldStartBlockAfterFenceClose = (currentText, incoming) => {
      if (!currentText || !incoming) return false;
      if (/^\s/.test(incoming)) return false;
      return getLastLine(currentText).trim() === '```';
    };

    const shouldInsertHeadingMarkerSpace = (currentText, incoming) => {
      if (!currentText || !incoming) return false;
      if (isInsideCodeFence(currentText)) return false;
      if (/^\s/.test(incoming)) return false;

      // CommonMark headings require a space after '#'.
      // Streams can split this as "###" + "2" + ")" + " Title".
      return /(^|\n)#{1,6}$/.test(currentText);
    };

    /**
     * Token streams often emit one word (or fragment) per SSE event without spaces.
     * Insert a single space at token boundaries only when both sides read as "stuck"
     * text, with guards for years, acronyms, hyphenated/stream fragments, and URLs.
     */
    const shouldInsertInterTokenSpace = (currentText, incoming, prevChunk) => {
      if (!currentText || !incoming) return false;
      if (isInsideCodeFence(currentText)) return false;
      if (endsInsideUrl(currentText)) return false;
      if (/^\s/.test(incoming)) return false;

      const prevTail = currentText.slice(-1);
      const incHead = incoming.charAt(0);
      if (!prevTail || !incHead) return false;
      if (/\s/.test(prevTail)) return false;

      const prevWordish = /[A-Za-z0-9]/.test(prevTail);
      const incWordish = /[A-Za-z0-9]/.test(incHead);
      if (!prevWordish || !incWordish) return false;

      // Merged numeric literal (202 + 6)
      if (/\d/.test(prevTail) && /\d/.test(incHead)) {
        return false;
      }

      const pc = prevChunk || '';
      const pl = pc.length;
      const il = incoming.length;

      // N + RR, I + CRM-style acronym continuation
      if (pl === 1 && /^[A-Z]+$/.test(incoming) && incoming.length >= 2) {
        return false;
      }

      // P + ERSON / single-letter + mixed-case continuation
      if (pl === 1 && /[A-Z]/.test(pc) && /[A-Z]/.test(incHead) && /[a-z]/.test(incoming.slice(1))) {
        return false;
      }

      // A/I first-person or article only (not w+ired, R+udderStack, t+ighten, S+aaS)
      if (pl === 1 && /^[AaIi]$/.test(pc) && /[a-z]/.test(incHead)) {
        return true;
      }

      // companyTITLE, ValuePERSON
      if (pl >= 2 && il >= 2 && /[a-z]/.test(prevTail) && /[A-Z]/.test(incHead)) {
        return true;
      }

      // POST BODY (avoid SUB+RE short caps fragments → SUBREDDIT)
      if (pl >= 4 && il >= 4 && /^[A-Z]+$/.test(pc) && /^[A-Z]+$/.test(incoming)) {
        return true;
      }

      // PERSON A, OPTIONS A (labels split across tokens)
      if (pl >= 4 && il === 1 && /^[A-Z]+$/.test(pc) && /^[A-Z]$/.test(incoming)) {
        return true;
      }

      // market size — long all-lowercase tokens only (skip lag+gy)
      if (pl >= 3 && il >= 3 && /^[a-z]+$/.test(pc) && /^[a-z]+$/.test(incoming)) {
        return true;
      }

      // in2026, at1:500, Version 2
      if (/[A-Za-z]/.test(prevTail) && /\d/.test(incHead)) {
        return true;
      }

      // 30days, Gainsight line breaks
      if (/\d/.test(prevTail) && /[A-Za-z]/.test(incHead)) {
        return true;
      }

      // pts C — section labels; skip Saa+S → SaaS
      if (pl >= 2 && il === 1 && /[a-z]/.test(prevTail) && /[A-Z]/.test(incHead)) {
        if (incHead === 'S' && /aa$/i.test(pc)) return false;
        return true;
      }

      return false;
    };

    const appendStreamText = (delta) => {
      if (!delta) return;

      // Bind SSE tokens: optional inter-token space when the stream omits gaps,
      // plus markdown-syntactic normalizations (headings, fences, blocks).
      let normalizedDelta = delta;
      if (shouldPrefixMarkdownNewline(fullMessage, delta)) {
        const thematicOnly = /^-{3,}\s*$/.test(String(delta).trim());
        normalizedDelta =
          thematicOnly && fullMessage.trim() !== '' ? `\n\n${delta}` : `\n${delta}`;
      } else if (shouldPrefixNewlineAfterDecorativeLine(fullMessage, delta)) {
        normalizedDelta = `\n${delta}`;
      } else if (shouldPrefixNewlineAfterLoneEmphasisDelimiter(fullMessage, delta)) {
        normalizedDelta = `\n${delta}`;
      } else if (shouldPrefixNewlineAfterUrl(fullMessage, delta)) {
        normalizedDelta = `\n\n${delta}`;
      }

      const needsTokenSpace =
        normalizedDelta === delta &&
        shouldInsertInterTokenSpace(fullMessage, normalizedDelta, lastStreamChunk);
      if (needsTokenSpace) {
        normalizedDelta = ` ${normalizedDelta}`;
      }

      if (shouldInsertHeadingMarkerSpace(fullMessage, normalizedDelta)) {
        normalizedDelta = ` ${normalizedDelta}`;
      }

      if (shouldStartCodeBodyAfterFenceLanguage(fullMessage, normalizedDelta)) {
        normalizedDelta = `\n${normalizedDelta}`;
      }

      if (shouldStartParagraphAfterHeading(fullMessage, normalizedDelta)) {
        normalizedDelta = `\n\n${normalizedDelta}`;
      }

      if (shouldStartBlockAfterFenceClose(fullMessage, normalizedDelta)) {
        normalizedDelta = `\n\n${normalizedDelta}`;
      }

      fullMessage += normalizedDelta;
      lastStreamChunk = delta;
      scheduleOnMessage(normalizedDelta);
    };

    /** Unprefixed lines of a multi-line answer: already whole lines, so skip token heuristics. */
    const appendContinuationText = (text) => {
      if (!text) return;
      fullMessage += text;
      lastStreamChunk = text;
      scheduleOnMessage(text);
    };

    const processEventData = async (rawData, meta = {}) => {
      if (rawData == null) return;

      if (typeof rawData === 'string' && rawData.trim() === '[DONE]') {
        return;
      }

      // Preserve explicit empty `data:` events as newline tokens.
      // Some backends emit blank SSE data events to represent paragraph/code/table line breaks.
      if (
        meta.dataLineCount > 0 &&
        meta.emptyDataLineCount === meta.dataLineCount
      ) {
        const newlineChunk = '\n'.repeat(meta.emptyDataLineCount);
        fullMessage += newlineChunk;
        scheduleOnMessage(newlineChunk);
        return;
      }

      const parsed = tryParseJsonDocument(rawData);

      // Cancellation and limit outcomes arrive as the inner object, a question
      // as its own typed frame — all three end the run and are never answer text.
      if (dispatchQualification(parsed, qualificationCallbacks)) {
        return;
      }

      // HTTP 200 followed by a bare error document: a failure, not content.
      if (isBareErrorDocument(parsed)) {
        const bareError = new Error(parsed.error);
        bareError.data = parsed;
        if (parsed.status_code) bareError.status = parsed.status_code;
        streamAbortedByErrorEvent = true;
        isClosed = true;
        flushOnMessagePending();
        try {
          await reader.cancel();
        } catch (_) {}
        onError?.(bareError);
        return;
      }

      if (isUntypedJsonDocument(parsed)) {
        appendStreamText(formatJsonDocument(parsed));
        return;
      }

      // Handle structured events
      if (parsed?.type) {
        if (parsed.type === 'error') {
          const err = new Error(parsed.error || parsed.message || 'Stream error');
          if (parsed.code) err.code = parsed.code;
          if (parsed.status) err.status = parsed.status;
          err.data = parsed;
          streamAbortedByErrorEvent = true;
          isClosed = true;
          flushOnMessagePending();
          try {
            await reader.cancel();
          } catch (_) {}
          onError?.(err);
          return;
        }

        if (hilCallbacks) {
          if (parsed.type === 'hil_step_started' && hilCallbacks.onStepStarted) {
            hilCallbacks.onStepStarted(parsed);
            return;
          }
          if (parsed.type === 'hil_step_completed' && hilCallbacks.onStepCompleted) {
            hilCallbacks.onStepCompleted(parsed);
            return;
          }
          if (parsed.type === 'hil_pause' && hilCallbacks.onPause) {
            hilCallbacks.onPause(parsed);
            return;
          }
          if (parsed.type === 'hil_completed' && hilCallbacks.onCompleted) {
            hilCallbacks.onCompleted(parsed);
            return;
          }
          if (parsed.type === 'hil_error' && hilCallbacks.onError) {
            hilCallbacks.onError(parsed);
            return;
          }
        }

        if (
          [
            'team_started',
            'member_started',
            'member_chunk',
            'member_completed',
            'tool_call_started',
            'tool_call_completed',
            'team_synthesizing',
            'team_completed',
            'team_error',
          ].includes(parsed.type) &&
          teamCallbacks?.onTeamEvent
        ) {
          teamCallbacks.onTeamEvent(parsed);
          return;
        }

        if ((parsed.type === 'workflow_background_started' || parsed.type === 'team_background_started') && onBackgroundStarted) {
          onBackgroundStarted(parsed);
          return;
        }

        // Loop node events: pre-loop chain steps + background batch job creation
        if (
          (parsed.type === 'loop_pre_step_started' || parsed.type === 'loop_pre_step_completed') &&
          loopCallbacks?.onLoopPreStep
        ) {
          loopCallbacks.onLoopPreStep(parsed);
          return;
        }

        if (parsed.type === 'batch_job_started') {
          if (parsed.run_id) {
            emitRunMeta({ run_id: parsed.run_id }, 'sse');
          }
          if (loopCallbacks?.onBatchJobStarted) {
            loopCallbacks.onBatchJobStarted(parsed);
          }
          return;
        }

        if (parsed.type === 'run_meta') {
          emitRunMeta(parsed, 'sse');
          return;
        }

        if (parsed.type === 'tool_warning') {
          onToolWarning?.(parsed);
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('agent:tool-warning', { detail: parsed }));
            }
          } catch (_) {}
          return;
        }

        // Knowledge-base citations. Arrives after the answer text, best-first,
        // capped at 25 by the backend. Only `id` is guaranteed to be present.
        if (parsed.type === 'sources') {
          onSources?.(Array.isArray(parsed.sources) ? parsed.sources : []);
          return;
        }

        // Suggested next questions. Arrives last, only when the feature is on.
        if (parsed.type === 'followups') {
          onFollowups?.(Array.isArray(parsed.items) ? parsed.items : []);
          return;
        }

        // regenerate/fork/background failure mid-stream. `message` is written
        // for end users, so it is surfaced verbatim.
        if (parsed.type === 'run_error') {
          const runError = new Error(parsed.message || 'The run could not be continued.');
          runError.data = parsed;
          runError.isRunError = true;
          streamAbortedByErrorEvent = true;
          isClosed = true;
          flushOnMessagePending();
          try {
            await reader.cancel();
          } catch (_) {}
          if (onRunError) {
            onRunError(parsed);
          } else {
            onError?.(runError);
          }
          return;
        }

        let streamed = null;
        if (typeof parsed.content === 'string') streamed = parsed.content;
        else if (typeof parsed.token === 'string') streamed = parsed.token;
        else if (typeof parsed.message === 'string') streamed = parsed.message;

        if (streamed != null && streamed !== '') {
          appendStreamText(streamed);
          return;
        }

        return;
      }

      // Raw token append
      appendStreamText(rawData);
    };

    try {
      // Process the stream
      while (true) {
        const { done, value } = await reader.read();

        if (isClosed) {
          break;
        }

        if (done) {
          break;
        }

        // Decode the chunk
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep the last incomplete line in buffer

        for (const rawLine of lines) {
          const result = sseAssembler.pushLine(rawLine);

          if (result.kind === 'event') {
            await processEventData(result.payload, result.meta);
          } else if (result.kind === 'text-continuation') {
            appendContinuationText(result.text);
          }

          if (streamAbortedByErrorEvent || isClosed) break;
        }

        if (streamAbortedByErrorEvent || isClosed) {
          break;
        }
      }

      // Process any trailing buffered line and/or incomplete pending event
      if (!streamAbortedByErrorEvent && !isClosed) {
        if (buffer) {
          const tailResult = sseAssembler.pushLine(buffer);
          if (tailResult.kind === 'text-continuation') {
            appendContinuationText(tailResult.text);
          }
          buffer = '';
        }

        const trailingEvent = sseAssembler.flush();
        if (trailingEvent) {
          await processEventData(trailingEvent.payload, trailingEvent.meta);
        }
      }

      if (streamAbortedByErrorEvent) {
        return;
      }

      flushOnMessagePending();

      // Stream completed
      if (onComplete) {
        onComplete(fullMessage);
      }
    } finally {
      if (onMessageFlushTimer) {
        clearTimeout(onMessageFlushTimer);
        onMessageFlushTimer = null;
      }
      flushOnMessagePending();
      if (sessionId) {
        this.activeStreams.delete(sessionId);
      }
    }
  }

  /**
   * Stream a run continuation (regenerate / fork).
   *
   * Both endpoints answer with the same SSE shape as a normal run, opening with a
   * `run_meta` frame that carries `continued_from`. They require the agent to have
   * memory + checkpointing on; otherwise the backend replies with a user-facing
   * error detail, which is surfaced through `onError`.
   *
   * @param {string} path - API path relative to the base URL
   * @param {Object|null} body - JSON body, or null to POST without one
   * @param {Object} options - same callbacks as consumeAgentStream
   */
  async continueRunStream(path, body, options = {}) {
    const { sessionId = null } = options;

    try {
      if (sessionId && this.activeStreams.has(sessionId)) {
        this.closeStream(sessionId);
      }

      const response = await fetch(`${config.API_BASE_URL}${path}`, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'x-api-key': getApiKey(),
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });

      if (!response.ok) {
        // The backend writes these details for end users ("Run not found. ...",
        // "This agent does not have checkpointing enabled"), so pass them through.
        let detail = null;
        let errorBody = null;
        try {
          errorBody = await response.json();
          detail = errorBody?.detail || errorBody?.message || errorBody?.error || null;
        } catch (_) {}
        const lockoutKind = detectLockout(response.status, errorBody);
        if (lockoutKind) signalLockout(lockoutKind);
        const httpError = new Error(detail || `HTTP error! status: ${response.status}`);
        httpError.status = response.status;
        httpError.data = errorBody;
        throw httpError;
      }

      return await this.consumeAgentStream(response, { ...options, streaming: true });
    } catch (error) {
      console.error('Run continuation stream error:', error);
      options.onError?.(error);
      if (sessionId) {
        this.activeStreams.delete(sessionId);
      }
    }
  }

  /**
   * Close an active stream
   * @param {string} sessionId - Session ID of the stream to close
   */
  closeStream(sessionId) {
    if (this.activeStreams.has(sessionId)) {
      const stream = this.activeStreams.get(sessionId);
      try {
        stream.reader.cancel();
      } catch (error) {
        console.warn('Error closing stream:', error);
      }
      this.activeStreams.delete(sessionId);
    }
  }

  /**
   * Close all active streams
   */
  closeAllStreams() {
    for (const [, stream] of this.activeStreams) {
      try {
        stream.reader.cancel();
      } catch (error) {
        console.warn('Error closing stream:', error);
      }
    }
    this.activeStreams.clear();
  }

  /**
   * Process HIL workflow SSE stream
   * @param {Response} response - Fetch response object
   * @param {Object} callbacks - Callbacks for HIL events
   * @returns {Promise} - Promise that resolves when stream completes
   */
  async processHILWorkflowStream(response, callbacks = {}) {
    const {
      onRunMeta,
      onStepStarted,
      onStepCompleted,
      onPause,
      onCompleted,
      onError: onHILError,
      onTeamEvent,
      qualificationCallbacks = null,
    } = callbacks;

    try {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.trim() === '') continue;
          
          if (line.startsWith('data:')) {
            const data = line.slice(5).replace(/^ /, '');
            if (data.trim() === '') continue;

            try {
              const parsed = JSON.parse(data);

              // Dedicated HIL start always streams, and a qualifying question
              // comes back before the workflow runs.
              if (dispatchQualification(parsed, qualificationCallbacks)) {
                continue;
              }

              if (parsed.type === 'run_meta' && onRunMeta) {
                onRunMeta(parsed);
              } else if (parsed.type === 'hil_step_started' && onStepStarted) {
                onStepStarted(parsed);
              } else if (parsed.type === 'hil_step_completed' && onStepCompleted) {
                onStepCompleted(parsed);
              } else if (parsed.type === 'hil_pause' && onPause) {
                onPause(parsed);
              } else if (parsed.type === 'hil_completed' && onCompleted) {
                onCompleted(parsed);
              } else if (parsed.type === 'hil_error' && onHILError) {
                onHILError(parsed);
              } else if (
                [
                  'team_started',
                  'team_synthesizing',
                  'team_completed',
                  'team_error',
                  'tool_call_started',
                  'tool_call_completed',
                ].includes(parsed.type) &&
                onTeamEvent
              ) {
                onTeamEvent(parsed);
              }
            } catch (e) {
              // Not JSON, ignore
              console.warn('Failed to parse SSE event:', data);
            }
          }
        }
      }
    } catch (error) {
      console.error('HIL workflow stream error:', error);
      if (onHILError) {
        onHILError({ message: error.message || 'Stream error' });
      }
      throw error;
    }
  }

  /**
   * Generate a unique session ID
   * @returns {string} - Generated session ID
   */
  generateSessionId() {
    return 'session_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }

}

// Create and export singleton instance
const streamingService = new StreamingService();
export default streamingService;
